import { useEffect, useState } from 'react';

export interface DashboardPollingOptions {
  // Whether any pipeline run is currently in flight. Drives the active
  // polling cadence — when false the dashboard relies on react-query staleTime
  // alone and stops issuing background requests.
  isAnyRunActive: boolean;
  // Polling cadence while a run is active, in milliseconds. Plan §4 prescribes
  // 10–15s; the default lands in the middle.
  intervalMs?: number;
}

export interface DashboardPollingState {
  // Effective react-query refetchInterval (or false to stop polling). Passing
  // a function to react-query lets us re-evaluate visibility per tick, but
  // the page-level visibility toggle is already covered by `enabled`, so a
  // plain number/false works.
  refetchInterval: number | false;
  // Whether the polling indicator should render in the header — true when we
  // are actively polling AND the tab is visible.
  isPolling: boolean;
  // Exposed for the header copy ("refreshes every 12s") so the static i18n
  // string stays in sync with whatever `intervalMs` resolves to.
  intervalSeconds: number;
}

// Pause polling when the tab is hidden. Without this the dashboard keeps
// hammering the API in background tabs — costly for the backend and noisy
// in dev tools. We listen to `visibilitychange` once and recompute state.
function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(() => {
    if (typeof document === 'undefined') return true;
    return document.visibilityState !== 'hidden';
  });
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

// Resolves the react-query refetchInterval for the dashboard's queries.
// Polling activates only when (a) at least one run is in flight, and (b) the
// tab is visible. Passing the result straight into useQuery's
// `refetchInterval` keeps cadence local to this hook — pages don't need their
// own setInterval bookkeeping.
export function useDashboardPolling({
  isAnyRunActive,
  intervalMs = 12_000
}: DashboardPollingOptions): DashboardPollingState {
  const visible = useDocumentVisible();
  const isPolling = isAnyRunActive && visible;
  return {
    refetchInterval: isPolling ? intervalMs : false,
    isPolling,
    intervalSeconds: Math.round(intervalMs / 1000)
  };
}
